// Date formatting helpers (ported from original)
export const today = () => {
  // Retorna a data local do DISPOSITIVO no formato YYYY-MM-DD,
  // sem usar toISOString() que converteria pra UTC.
  // O fuso do dispositivo é o correto para registros de campo:
  // com o fuso fixado em America/Sao_Paulo, obras no AM/AC/MT
  // (UTC-4/-5) gravavam a data do dia seguinte perto da meia-noite.
  const now = new Date()
  const fmt = new Intl.DateTimeFormat('en-CA') // 'en-CA' usa formato YYYY-MM-DD nativamente
  return fmt.format(now)
}

export const fd = (d: string | null | undefined) => {
  if (!d) return '—'
  const [y, m, dd] = String(d).split('T')[0].split('-')
  return `${dd}/${m}/${y}`
}

export const fdt = (d: string | null | undefined) => {
  if (!d) return '—'
  // Exibe no fuso do dispositivo — mesmo critério de today()
  return new Date(d).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

export const fm = (v: number | null | undefined) =>
  v != null ? 'R$ ' + Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '—'

export const esc = (s: string | null | undefined) =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

// ==========================================
// PERFIS - centralized profile definitions
// ==========================================

// Map old profile names to new ones
export const PERFIL_MAP: Record<string, string> = {
  diretoria: 'dono',
  coordenador: 'gerente',
  mestre: 'encarregado',
  tecnico_sst: 'tst',
  almoxarife: 'administrativo',
}

export const normalizePerfil = (perfil: string): string =>
  PERFIL_MAP[perfil] || perfil

// Profile hierarchy for display/ordering (canonical list — single source of truth)
export const PERFIS_LISTA = [
  { key: 'dono', label: 'Dono' },
  { key: 'gerente', label: 'Gerente' },
  { key: 'engenheiro', label: 'Engenheiro' },
  { key: 'administrativo', label: 'Administrativo' },
  { key: 'tst', label: 'TST' },
  { key: 'encarregado', label: 'Encarregado' },
  { key: 'empreiteiro', label: 'Empreiteiro' },
  { key: 'assistente', label: 'Assistente' },
  { key: 'visualizador', label: 'Visualizador' },
  { key: 'cliente', label: 'Cliente' },
]

// Quick lookup: key → label (includes legacy/technical aliases)
export const PERFIL_LABEL: Record<string, string> = {
  ...Object.fromEntries(PERFIS_LISTA.map(p => [p.key, p.label])),
  // Technical / legacy role aliases displayed in human-readable form
  super_admin: 'Administrador',
  superadmin: 'Administrador',
  admin: 'Administrador',
  gestor: 'Gestor',
  tecnico: 'Técnico',
  operario: 'Operário',
}

/** Format any perfil/cargo string into a human-readable label. */
export const formatPerfil = (p?: string | null) => {
  if (!p) return '—'
  const key = String(p).trim().toLowerCase()
  return PERFIL_LABEL[key] || PERFIL_LABEL[normalizePerfil(key)] || p
}

// Profiles with management capabilities
export const PERFIS_GESTAO = ['dono', 'gerente', 'engenheiro']
export const PERFIS_CAMPO = ['encarregado', 'tst', 'empreiteiro', 'assistente']
export const PERFIS_TODOS = [...PERFIS_GESTAO, ...PERFIS_CAMPO, 'administrativo', 'visualizador']

// Legacy aliases for backward compatibility
export const PERFIS_GESTOR = PERFIS_GESTAO
export const PERFIS_TODOS_LEGACY = PERFIS_TODOS

// ==========================================
// LEGACY PERM — @deprecated dead code
// ==========================================
// hasperm() nunca é chamado fora deste arquivo.
// Sistema de permissões atual (fonte da verdade): usePerfilPermissions.ts (DB)
// Hierarquia de sistemas:
//   1. mobile/permissions.ts   → visibilidade de menu mobile (hardcoded por perfil)
//   2. usePerfilPermissions.ts → permissões por módulo no DB (fonte da verdade)
//   3. usePermissions.ts       → delega para (2) + adiciona booleanos de company_role
//   4. PERM/hasperm abaixo     → dead code, nunca chamado — candidato à remoção
// ==========================================
export const PERM: Record<string, string[]> = {
  dashboard: PERFIS_TODOS,
  'obra-dash': PERFIS_TODOS,
  obras: PERFIS_GESTAO,
  diario: [...PERFIS_GESTAO, 'encarregado', 'empreiteiro', 'assistente', 'administrativo'],
  fotos: [...PERFIS_GESTAO, 'encarregado', 'tst', 'empreiteiro', 'assistente'],
  producao: [...PERFIS_GESTAO, 'encarregado', 'empreiteiro', 'assistente'],
  fvs: [...PERFIS_GESTAO, 'encarregado', 'assistente'],
  fvm: [...PERFIS_GESTAO, 'administrativo', 'assistente'],
  pendencias: [...PERFIS_GESTAO, 'encarregado', 'tst', 'administrativo', 'assistente'],
  ocorrencias: [...PERFIS_GESTAO, 'encarregado', 'tst', 'empreiteiro', 'assistente'],
  cronograma: [...PERFIS_GESTAO, 'administrativo'],
  sst: [...PERFIS_GESTAO, 'encarregado', 'tst'],
  apr: [...PERFIS_GESTAO, 'tst'],
  pt: [...PERFIS_GESTAO, 'tst'],
  apt: [...PERFIS_GESTAO, 'tst'],
  dds: [...PERFIS_GESTAO, 'encarregado', 'tst', 'assistente'],
  acidentes: [...PERFIS_GESTAO, 'encarregado', 'tst'],
  inspecoes: [...PERFIS_GESTAO, 'tst'],
  planta: PERFIS_GESTAO,
  documentos: PERFIS_TODOS,
  historico: [...PERFIS_GESTAO, 'administrativo'],
  concreto: PERFIS_GESTAO,
  revestimentos: PERFIS_GESTAO,
  entregas: [...PERFIS_GESTAO, 'administrativo'],
  estoque: [...PERFIS_GESTAO, 'administrativo', 'encarregado'],
  identidade: [...PERFIS_GESTAO, 'administrativo'],
  equipe: [...PERFIS_GESTAO, 'administrativo'],
  chat: PERFIS_TODOS,
  relatorios: PERFIS_TODOS,
  'assistente-ia': [...PERFIS_GESTAO, 'tst', 'encarregado', 'assistente'],
  'avaliacao-fornecedores': [...PERFIS_GESTAO, 'administrativo'],
}

/** @deprecated Nunca chamado. Use usePerfilPermissions() para verificar permissões. */
export const hasperm = (pg: string, perfil?: string) => {
  if (!perfil) return false
  const normalized = normalizePerfil(perfil)
  if (PERFIS_GESTAO.includes(normalized)) return true
  return (PERM[pg] || []).includes(normalized)
}

// Check if a module is active for a user (dono always has all modules)
export const hasModulo = (modulo: string, perfil?: string, modulos_ativos?: string[]) => {
  const normalized = normalizePerfil(perfil || '')
  if (normalized === 'dono' || normalized === 'gerente') return true
  if (!modulos_ativos || modulos_ativos.length === 0) return false
  return modulos_ativos.includes(modulo)
}

// All available modules for the checkbox UI
export const MODULOS_DISPONIVEIS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'obra-dash', label: 'Dashboard da Obra' },
  { key: 'obras', label: 'Obras' },
  { key: 'dailyin', label: 'Daily In' },
  { key: 'diario', label: 'Diário / RDO' },
  { key: 'fotos', label: 'Fotos & Mídia' },
  { key: 'producao', label: 'Produção' },
  { key: 'fvs', label: 'FVS / Qualidade' },
  { key: 'fvm', label: 'FVM / Materiais' },
  { key: 'pendencias', label: 'Pendências' },
  { key: 'ocorrencias', label: 'Ocorrências' },
  { key: 'concreto', label: 'Concreto' },
  { key: 'revestimentos', label: 'Revestimentos' },
  { key: 'entregas', label: 'Entrega Unidades' },
  { key: 'estoque', label: 'Estoque' },
  { key: 'planta', label: 'Planta da Obra' },
  { key: 'documentos', label: 'Documentos Técnicos' },
  { key: 'cronograma', label: 'Cronograma' },
  { key: 'sst', label: 'Segurança SST' },
  { key: 'apr', label: 'APR' },
  { key: 'pt', label: 'Permissão Trabalho' },
  { key: 'apt', label: 'APT' },
  { key: 'dds', label: 'DDS' },
  { key: 'acidentes', label: 'Acidentes' },
  { key: 'inspecoes', label: 'Inspeções' },
  { key: 'historico', label: 'Histórico' },
  { key: 'identidade', label: 'Identidade' },
  { key: 'equipe', label: 'Equipe' },
  { key: 'chat', label: 'Chat' },
  { key: 'relatorios', label: 'Relatórios' },
  { key: 'assistente-ia', label: 'Assistente IA' },
  { key: 'avaliacao-fornecedores', label: 'Avaliação Fornecedores' },
]

export const podeEditar = (perfil?: string) => {
  const normalized = normalizePerfil(perfil || '')
  return [...PERFIS_GESTAO, 'tst', 'encarregado', 'administrativo'].includes(normalized)
}

export const isGestor = (perfil?: string) => {
  const normalized = normalizePerfil(perfil || '')
  return ['dono', 'gerente'].includes(normalized)
}

// Status labels
export const STATUS_OBRA: Record<string, string> = {
  em_execucao: 'Em execução',
  planejamento: 'Planejamento',
  pausada: 'Pausada',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
}

export const CLIMA_ICO: Record<string, string> = {
  ensolarado: '☀️',
  parcialmente_nublado: '⛅',
  nublado: '☁️',
  chuvoso: '🌧',
  tempestade: '⛈',
}

export const CLIMA_LBL: Record<string, string> = {
  ensolarado: 'Ensolarado',
  parcialmente_nublado: 'Parc. nublado',
  nublado: 'Nublado',
  chuvoso: 'Chuvoso',
  tempestade: 'Tempestade',
}
